import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Building, Users, Package, Truck, CheckCircle, Clock, TrendingUp, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500',
  pickup: 'bg-blue-500',
  processed: 'bg-orange-500',
  delivered: 'bg-green-500',
};

const PGAnalytics = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSuperAdmin()) { navigate('/dashboard'); return; }
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/pg-analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!data) return <p className="text-center text-slate-500">Failed to load analytics</p>;

  const maxDailyOrders = Math.max(...data.daily_orders.map(d => d.orders), 1);

  return (
    <div className="space-y-6" data-testid="pg-analytics-page">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total PGs', value: data.total_pgs, icon: Building, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Customers', value: data.total_customers, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Total Orders', value: data.total_orders, icon: Package, color: 'text-orange-600', bg: 'bg-orange-50' },
          { label: 'Clothes Processed', value: data.total_clothes, icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${s.bg}`}>
                    <Icon className={`w-5 h-5 ${s.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{s.value}</p>
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Status Distribution */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-600" />
              Order Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { key: 'pending', label: 'Pending', icon: Clock, color: 'bg-yellow-500' },
                { key: 'pickup', label: 'Picked Up', icon: Truck, color: 'bg-blue-500' },
                { key: 'processed', label: 'Processed', icon: Package, color: 'bg-orange-500' },
                { key: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'bg-green-500' },
              ].map((s) => {
                const count = data.status_distribution[s.key] || 0;
                const pct = data.total_orders > 0 ? (count / data.total_orders) * 100 : 0;
                const Icon = s.icon;
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-700">{s.label}</span>
                        <span className="text-sm font-semibold text-slate-800">{count}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className={`${s.color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Daily Orders (Last 7 Days) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              Orders (Last 7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-40">
              {data.daily_orders.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-semibold text-slate-700">{day.orders}</span>
                  <div className="w-full bg-slate-100 rounded-t-md relative" style={{ height: '120px' }}>
                    <div
                      className="absolute bottom-0 w-full bg-indigo-500 rounded-t-md transition-all"
                      style={{ height: `${(day.orders / maxDailyOrders) * 100}%`, minHeight: day.orders > 0 ? '8px' : '0' }}
                    />
                  </div>
                  <span className="text-[10px] text-slate-500">{day.date}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-PG Breakdown */}
      {/* Mobile: Cards */}
      <div className="space-y-3 md:hidden">
        {data.pg_stats.map((pg) => (
          <Card key={pg.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-slate-800">{pg.name}</p>
                <Badge className={pg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {pg.status}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-slate-800">{pg.members}</p>
                  <p className="text-[10px] text-slate-500">Members</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-slate-800">{pg.total_orders}</p>
                  <p className="text-[10px] text-slate-500">Orders</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-slate-800">{pg.total_clothes}</p>
                  <p className="text-[10px] text-slate-500">Clothes</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Badge className="bg-yellow-100 text-yellow-700">{pg.pending} pending</Badge>
                <Badge className="bg-blue-100 text-blue-700">{pg.in_progress} in progress</Badge>
                <Badge className="bg-green-100 text-green-700">{pg.delivered} delivered</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop: Table */}
      <Card className="hidden md:block">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building className="w-4 h-4 text-indigo-600" />
            PG Breakdown
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pg-analytics-table">
            <thead className="bg-slate-50 border-y">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">PG Name</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Members</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Orders</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Pending</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">In Progress</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Delivered</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">Clothes</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.pg_stats.map((pg) => (
                <tr key={pg.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{pg.name}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge className={pg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {pg.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-700">{pg.members}</td>
                  <td className="px-4 py-3 text-center font-semibold text-slate-800">{pg.total_orders}</td>
                  <td className="px-4 py-3 text-center"><Badge className="bg-yellow-100 text-yellow-700">{pg.pending}</Badge></td>
                  <td className="px-4 py-3 text-center"><Badge className="bg-blue-100 text-blue-700">{pg.in_progress}</Badge></td>
                  <td className="px-4 py-3 text-center"><Badge className="bg-green-100 text-green-700">{pg.delivered}</Badge></td>
                  <td className="px-4 py-3 text-center text-slate-700">{pg.total_clothes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default PGAnalytics;
